import queryString from 'query-string'
import React from 'react'

import ApiTrigger from 'components/common/ApiTrigger'
import Button from 'components/common/Button'
import { Column, Columns } from 'components/common/Grid'
import InputTextField from 'components/common/InputTextField'
import MFAImage from 'components/common/MFAImage'
import Text from 'components/common/Text'
import TextButton from 'components/common/TextButton'
import { Heading } from 'components/navigation/Header'
import toast from 'lib/toast'
import theme from 'theme'
import { UserAuth } from 'types/api/user/session'

const MFATokenEntry: React.FC<{
  mfaResponse: UserAuth
  validateMFASuccess: () => void
  onCancel?: () => void
  email: string
  isSignIn?: boolean
}> = (props) => {
  const { mfaResponse, validateMFASuccess, onCancel, email, isSignIn } = props
  const [mfaAuthToken, setMfaAuthToken] = React.useState('')

  return (
    <section>
      <section
        css={{
          marginTop: theme.space[5]
        }}
      >
        {!isSignIn ? (
          <Heading>Step 2: Scan this barcode with your app</Heading>
        ) : (
          <div>
            <strong
              css={{
                color: theme.colors.steel[1]
              }}
            >
              Step 2
            </strong>
            <Heading
              css={{
                marginTop: theme.space[1]
              }}
            >
              Scan this barcode with your app
            </Heading>
          </div>
        )}
        <Text as="p" css={{ marginTop: theme.space[3] }}>
          Multi-factor authentication has been enabled. You still need to set up
          your authenticator. <br />
          Do not leave this page until your authenticator is set up.
        </Text>
      </section>
      <Columns
        columns={4}
        css={{
          marginTop: theme.space[5] + theme.space[3],
          marginBottom: theme.space[5] + theme.space[3],
          alignItems: 'center'
        }}
      >
        <Column span={1}>
          <MFAImage
            data-test-mfa-response-qrurl-image={mfaResponse.qrUrl}
            errorMessage="We’re having trouble loading the QR code image"
            src={mfaResponse.qrUrl}
          />
        </Column>
        <Column
          css={{
            marginLeft: theme.space[7],
            marginTop: '0'
          }}
          span={3}
        >
          <Text
            css={{
              marginBottom: theme.space[1]
            }}
          >
            You can also set up your authenticator manually using the following
            secret key:
          </Text>
          <strong
            data-test-mfa-response-secret-key
            css={{
              color: theme.colors.steel[1]
            }}
          >
            {mfaResponse.secretKey}
          </strong>
        </Column>
      </Columns>
      <section>
        <Text
          as="h3"
          css={{
            color: theme.colors.steel[2],
            fontSize: theme.fontSizes[2],
            marginBottom: theme.space[2]
          }}
        >
          Enter the six-digits code from the application
        </Text>
        <Text as="p">
          After scanning the barcode image, the app will display a six-digit
          code that you can enter below:
        </Text>
        <InputTextField
          focusOnMount
          css={{ marginTop: theme.space[4], maxWidth: '245px' }}
          maxLength={6}
          name="mfaAuthToken"
          placeholder="123456"
          value={mfaAuthToken}
          onChange={(event) => setMfaAuthToken(event.target.value)}
        />
      </section>
      <section css={{ display: 'flex', flexDirection: 'row' }}>
        <ApiTrigger
          data-test-validate-mfa-button
          primary
          as={Button}
          disabled={mfaAuthToken.length !== 6}
          errorToastMessage="There was an issue validating your multi-factor authentication. Please try again later."
          request={{
            url: '/users/auth/mfa/validate',
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            data: queryString.stringify({
              email: email,
              token: mfaAuthToken
            })
          }}
          successToastMessage="You’re all setup for multi-factor authentication."
          onFailure={({ response }) => {
            toast({
              type: 'error',
              text:
                response?.data ||
                'There was an issue validating multi-factor authentication. ' +
                  'You may have entered the wrong token. Please try again.'
            })
            setMfaAuthToken('')
          }}
          onSuccess={() => validateMFASuccess()}
        >
          Enable
        </ApiTrigger>
        {!isSignIn && (
          <TextButton
            css={{ fontSize: theme.fontSizes[1], marginLeft: theme.space[5] }}
            onClick={() => onCancel()}
          >
            I&apos;ve changed my mind.
          </TextButton>
        )}
      </section>
    </section>
  )
}

export default MFATokenEntry
